import { useEffect, useState } from "react";
import getUrl from "../../utility/getUrl";
import { AuthenticationInfoType } from "../../utility/types";
import { ProjectSummary, ProjectTimeLineItem } from "../PublishProject/types";

import "./ProjectSummaries.scss";
import ProjectTree from "./ProjectTree";
import { createProjectTree, ProjectTreeNode, sortProjectTree } from "./ProjectTreeUtil";
import Status from "../Status";
import ProjectDetails from "./ProjectDetails";

interface ProjectSummariesProps {
  auth: AuthenticationInfoType;
  organizationId: string;
}

const ProjectSummaries = ({ auth, organizationId }: ProjectSummariesProps) => {
  const [displayUTC, setDisplayUTC] = useState<boolean>(true);

  const [projectSummaries, setProjectSummaries] = useState<ProjectSummary[]>([]);
  const [projectTreeNodes, setProjectTreeNodes] = useState<ProjectTreeNode[]>([]);
  const [timeLine, setTimeLine] = useState<ProjectTimeLineItem[]>([]);

  const [currentProject, setCurrentProject] = useState<ProjectTreeNode | undefined>(undefined);
  const [status, setStatus] = useState("ab");

  useEffect(() => {
    const loadData = async () => {
      try {
        setStatus("");
        const url = getUrl("projectSummaries", organizationId.trim());
        const options = {
          headers: new Headers({
            Authorization: `Bearer ${auth.accessToken}`,
          }),
        };
        const response = await fetch(url, options);

        if (!response.ok) {
          let error = "Projects not found";
          if (response.status === 401) {
            error = "Sorry, you have no access to this service";
          }
          setStatus(error);
          return;
        }

        if (response.status !== 200) {
          throw new Error(`${response.status} loading projects`);
        }
        let summaries = (await response.json()) as ProjectSummary[];
        summaries = summaries.map((s) => {
          return {
            ...s,
            dateOfUpload: new Date(s.dateOfUpload),
            dateOfDeletion: s.dateOfDeletion ? new Date(s.dateOfDeletion) : undefined,
          };
        });

        setProjectSummaries(summaries);

        let treeNodes = createProjectTree(summaries);
        treeNodes = sortProjectTree(treeNodes);
        setProjectTreeNodes(treeNodes);
      } catch (err) {
        console.error(err);
        setStatus(`${err}`);
      }
    };
    loadData();
  }, [organizationId, auth]);

  const onClickProjectTree = (node: ProjectTreeNode) => {
    if (node.type === "project") {
      setCurrentProject(node);
      const projects = projectSummaries.filter((ps) => ps.name === node.text && ps.folder === node.projectFolder);

      const timeline: ProjectTimeLineItem[] = [];
      for (const project of projects) {
        if (project.dateOfDeletion) {
          timeline.push({
            id: project.id,
            organizationId: project.organizationId,
            version: project.version,
            date: project.dateOfDeletion,
            type: "deleted",
            active: !project.dateOfDeletion,
          });
        }
        timeline.push({
          id: project.id,
          organizationId: project.organizationId,
          version: project.version,
          date: project.dateOfUpload,
          type: "created",
          active: !project.dateOfDeletion,
        });
      }
      timeline.sort((a, b) => a.date.getTime() - b.date.getTime());
      // lebende projekte -> einen eintrag
      // geloeschte projekte -> zwei eintraege (erzeugt, geloescht)
      setTimeLine(timeline);
    }
  };

  if (status) {
    return (
      <div className="project-summaries">
        <Status msg={status}></Status>
      </div>
    );
  }

  return (
    <div className="project-summaries">
      <div className="project-tree">
        <ProjectTree
          nodes={projectTreeNodes}
          selectedNode={currentProject}
          onClick={onClickProjectTree}
          indent={false}
        ></ProjectTree>
      </div>
      <ProjectDetails
        auth={auth}
        currentProject={currentProject}
        displayUTC={displayUTC}
        onClickUTC={(on) => setDisplayUTC(on)}
        timeLine={timeLine}
      ></ProjectDetails>
    </div>
  );
};

export default ProjectSummaries;
